import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { usersData$, userRefreshing$ } from "../reduxStore/selectors/userSelectors";
import { logoutUser, refreshUser } from "../reduxStore/actions/userActionsHelper";
import { getAccessToken } from "../api/config";
import DemoOnboardingModal from "../components/demoOnboardingModal/DemoOnboardingModal";
import LoadingSpinner from "../components/loadingSpinner/loadingSpinner";
import MessageConstant from "../utils/Constant/MessageConstant";
import DemoExitModal from "../components/demoExitModal/DemoExitModal";

const IS_DEMO = process.env.REACT_APP_ENVCONFIG === "demo";

/**
 * Paths accessible when REACT_APP_ENVCONFIG=demo.
 * Any other path redirects to DEMO_FALLBACK_PATH.
 * Prefix-matched, so /user-full-test/:id is covered by "/user-full-test".
 */
export const DEMO_ALLOWED_PATHS = [
    "/user-dashboard",
    "/user-sat-practice",
    "/user-practice-session",
    "/user-full-test",
    "/user-setting",
];

const DEMO_PUBLIC_REDIRECT_PATHS = [
    "/",
    "/landing",
    "/sign-in",
    "/sign-up",
    "/confirmation",
    "/forgot-password",
    "/reset-password",
    "/pricing",
    "/about-us",
    "/terms-of-service",
    "/privacy-policy",
    "/refund-complaint-policy",
    "/link-expired",
];

const DEMO_FALLBACK_PATH = "/user-dashboard";

/**
 * DemoRoute — top-level guard for the demo environment.
 *
 * Non-demo env : renders children as-is (no restriction).
 * Demo env     :
 *   - No authenticated user → shows DemoOnboardingModal (blocks the entire UI).
 *   - Authenticated user    → enforces DEMO_ALLOWED_PATHS whitelist.
 */
export default function DemoRoute({ children }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const userData = useSelector(usersData$);
    const isRefreshing = useSelector(userRefreshing$);
    const token = getAccessToken();
    const [hasAttemptedRefresh, setHasAttemptedRefresh] = useState(false);
    const [isAuthInitialized, setIsAuthInitialized] = useState(!IS_DEMO);

    useEffect(() => {
        if (!IS_DEMO || hasAttemptedRefresh) return;

        setHasAttemptedRefresh(true);

        if (token) {
            setIsAuthInitialized(true);
            return;
        }

        dispatch(refreshUser.request({ retryCount: 0, maxRetries: 4 }));
    }, [dispatch, hasAttemptedRefresh, token]);

    useEffect(() => {
        if (!IS_DEMO || !hasAttemptedRefresh) return;
        if (token || !isRefreshing) {
            setIsAuthInitialized(true);
        }
    }, [hasAttemptedRefresh, isRefreshing, token]);

    useEffect(() => {
        if (!IS_DEMO || !isAuthInitialized) return;
        if (userData && !token) {
            dispatch(logoutUser.success());
        }
    }, [dispatch, isAuthInitialized, token, userData]);

    if (!IS_DEMO) return children;

    if (!isAuthInitialized) {
        return <LoadingSpinner message={MessageConstant.INITIALIZING} />;
    }

    // Not authenticated or token lost (e.g. page refresh after demo session) — show onboarding
    if (!userData || !token) {
        return <DemoOnboardingModal />;
    }

    // Enforce path whitelist
    const isAllowed = DEMO_ALLOWED_PATHS.some(
        (allowed) => pathname === allowed || pathname.startsWith(allowed + "/")
    );

    if (!isAllowed) {
        if (DEMO_PUBLIC_REDIRECT_PATHS.includes(pathname)) {
            return <Navigate to={DEMO_FALLBACK_PATH} replace />;
        }

        return (
            <DemoExitModal
                open={true}
                variant="restrictedArea"
                onPrimary={() => window.location.assign('https://studyprepal.com')}
                onSecondary={() => navigate(DEMO_FALLBACK_PATH, { replace: true })}
            />
        );
    }

    return children;
}

